import { useCallback, useEffect, useRef, useState } from "react";

export interface CountdownApi {
  /** Whole seconds remaining (rounded up for display). */
  remaining: number;
  /** Fraction elapsed, 0 → 1, for progress rings. */
  progress: number;
  running: boolean;
  finished: boolean;
  duration: number;
  start: () => void;
  pause: () => void;
  reset: () => void;
  /** Set a new duration (in seconds) and reset to it. */
  setDuration: (seconds: number) => void;
}

/**
 * Countdown timer. Remaining time is derived from an absolute end timestamp, so
 * it stays accurate even when the tab is backgrounded and interval ticks are
 * throttled. `onComplete` fires once when the timer reaches zero.
 */
export function useCountdown(
  initialSeconds: number,
  onComplete?: () => void,
): CountdownApi {
  const [duration, setDurationState] = useState(initialSeconds);
  const [remainingMs, setRemainingMs] = useState(initialSeconds * 1000);
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const endRef = useRef<number | null>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!running) return;
    const tick = () => {
      if (endRef.current == null) return;
      const ms = endRef.current - Date.now();
      if (ms <= 0) {
        setRemainingMs(0);
        setRunning(false);
        setFinished(true);
        endRef.current = null;
        onCompleteRef.current?.();
      } else {
        setRemainingMs(ms);
      }
    };
    const id = setInterval(tick, 100);
    tick();
    return () => clearInterval(id);
  }, [running]);

  const start = useCallback(() => {
    const secsLeft = remainingMs > 0 ? remainingMs / 1000 : duration;
    if (secsLeft <= 0) return;
    endRef.current = Date.now() + secsLeft * 1000;
    setFinished(false);
    setRunning(true);
  }, [remainingMs, duration]);

  const pause = useCallback(() => {
    setRunning(false);
    endRef.current = null;
  }, []);

  const reset = useCallback(() => {
    setRunning(false);
    setFinished(false);
    endRef.current = null;
    setRemainingMs(duration * 1000);
  }, [duration]);

  const setDuration = useCallback((seconds: number) => {
    setDurationState(seconds);
    setRemainingMs(seconds * 1000);
    setRunning(false);
    setFinished(false);
    endRef.current = null;
  }, []);

  return {
    remaining: Math.ceil(remainingMs / 1000),
    progress: duration > 0 ? 1 - remainingMs / 1000 / duration : 0,
    running,
    finished,
    duration,
    start,
    pause,
    reset,
    setDuration,
  };
}
